import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import { 
  Building2, 
  Plus, 
  Edit2, 
  Trash2, 
  Phone, 
  FileText,
  Users,
  Save,
  X,
  CheckCircle2,
  AlertTriangle,
  Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const ClientsManagement = () => {
  const { token, isMasterAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_phone: '',
    address: '',
    siret: '',
    gestionnaire_amiable_email: 'suma@c2-assurances.fr'
  });

  useEffect(() => {
    fetchClients();
  }, [token]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get claim counts for each organization
      const claimsResponse = await axios.get(`${API_URL}/claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const claimCounts = {};
      claimsResponse.data.forEach(claim => {
        const orgId = claim.organization_id || 'unknown';
        claimCounts[orgId] = (claimCounts[orgId] || 0) + 1;
      });
      
      const clientsWithCounts = response.data.map(org => ({
        ...org,
        claim_count: claimCounts[org.id] || 0
      }));
      
      setClients(clientsWithCounts);
    } catch (err) {
      console.error('Error fetching clients:', err);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/organizations/${editingClient.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API_URL}/organizations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client créé');
      }
      
      setShowForm(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (err) {
      console.error('Error saving client:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      contact_phone: client.contact_phone || '',
      address: client.address || '',
      siret: client.siret || '',
      gestionnaire_amiable_email: client.gestionnaire_amiable_email || 'suma@c2-assurances.fr'
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/organizations/${clientToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      setShowDeleteDialog(false);
      setClientToDelete(null);
      fetchClients();
    } catch (err) {
      console.error('Error deleting client:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (client) => {
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_phone: '',
      address: '',
      siret: '',
      gestionnaire_amiable_email: 'suma@c2-assurances.fr'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion des Clients</h1>
          <p className="text-slate-600">Gérez vos clients et leurs accès chauffeurs</p>
        </div>
        <Button 
          onClick={() => { setShowForm(true); setEditingClient(null); resetForm(); }}
          className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <Card className="border-2 border-[#1e3a5f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editingClient ? 'Modifier le client' : 'Nouveau client'}
            </CardTitle>
            <CardDescription>
              {editingClient 
                ? 'Modifiez les informations du client' 
                : 'Créez un nouveau client avec ses accès chauffeurs'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom du client */}
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du client *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: SUMA"
                    required
                    data-testid="client-name-input"
                  />
                </div>

                {/* SIRET */}
                <div className="space-y-2">
                  <Label htmlFor="siret">SIRET</Label>
                  <Input
                    id="siret"
                    value={formData.siret}
                    onChange={(e) => setFormData({...formData, siret: e.target.value})}
                    placeholder="123 456 789 00012"
                  />
                </div>

                {/* Téléphone */}
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Téléphone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>

                {/* Adresse */}
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 rue de l'exemple, 75000 Paris"
                  />
                </div>

                {/* Gestionnaire Amiable */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="gestionnaire_amiable_email">Email du gestionnaire amiable</Label>
                  <Input
                    id="gestionnaire_amiable_email"
                    type="email"
                    value={formData.gestionnaire_amiable_email}
                    onChange={(e) => setFormData({...formData, gestionnaire_amiable_email: e.target.value})}
                    placeholder="suma@c2-assurances.fr"
                    data-testid="gestionnaire-email-input"
                  />
                  <p className="text-xs text-slate-500">
                    Ce gestionnaire recevra les notifications par email pour les sinistres de ce client.
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-slate-500">
                  Les chauffeurs déclarent les sinistres directement via la PWA en entrant <strong>{formData.name || 'le nom de la société'}</strong>. Aucun mot de passe n'est requis.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); setEditingClient(null); resetForm(); }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
                  <Save className="w-4 h-4 mr-2" />
                  {editingClient ? 'Mettre à jour' : 'Créer le client'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Clients List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((client) => (
          <Card key={client.id} className="hover:shadow-lg transition-shadow" data-testid={`client-card-${client.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-bold">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{client.name}</CardTitle>
                    {client.siret && (
                      <p className="text-xs text-slate-500">SIRET: {client.siret}</p>
                    )}
                  </div>
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  <FileText className="w-3 h-3 mr-1" />
                  {client.claim_count} dossier{client.claim_count > 1 ? 's' : ''}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Contact info */}
              <div className="space-y-2 text-sm">
                {client.contact_phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    <span>{client.contact_phone}</span>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building2 className="w-4 h-4" />
                    <span className="truncate">{client.address}</span>
                  </div>
                )}
                {client.gestionnaire_amiable_email && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate text-xs">{client.gestionnaire_amiable_email}</span>
                  </div>
                )}
              </div>

              {/* Driver access */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium text-slate-500 uppercase">Accès Chauffeurs</p>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#1e3a5f]" />
                  <span className="text-sm font-medium">Connexion : {client.name}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="w-3 h-3" />
                  Accès configuré
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(client)}
                  className="flex-1"
                  data-testid={`edit-client-${client.id}`}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                {isMasterAdmin() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(client)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {clients.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">Aucun client</h3>
            <p className="text-slate-500">Créez votre premier client pour commencer</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Êtes-vous sûr de vouloir supprimer le client <strong>{clientToDelete?.name}</strong> ?</p>
              {clientToDelete?.claim_count > 0 && (
                <p className="text-red-600 font-medium">
                  ⚠️ Attention : {clientToDelete.claim_count} dossier(s) seront également supprimés !
                </p>
              )}
              <p className="text-sm">Cette action est irréversible.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setClientToDelete(null);
              }}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client-btn"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
